import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Spinner, Alert } from "react-bootstrap";

const BannerSection = () => {
  const [bannerData, setBannerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentText, setCurrentText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(150);
  const [deletingSpeed, setDeletingSpeed] = useState(50);
  const [pauseDuration, setPauseDuration] = useState(1000);

  const timeoutRef = useRef(null);

  // Updated words to match AI product content generation
  const defaultWords = [
    "SEO Optimization",
    "Faster Production",
    "Higher Conversions",
    "Time Efficiency",
  ];

  // Fetch banner data from API
  useEffect(() => {
    const fetchBannerData = async () => {
      try {
        const response = await axios.get(
          "https://ecom-22-fe6311cae608.herokuapp.com/api/banner-section"
        );
        if (response.data.success) {
          setBannerData(response.data.data);
          // Set typing effect parameters from API if available
          if (response.data.data.typingSpeed) {
            setTypingSpeed(response.data.data.typingSpeed);
          }
          if (response.data.data.deletingSpeed) {
            setDeletingSpeed(response.data.data.deletingSpeed);
          }
          if (response.data.data.pauseDuration) {
            setPauseDuration(response.data.data.pauseDuration);
          }
        } else {
          setError("Failed to fetch banner data");
        }
      } catch (err) {
        setError("Error fetching banner data");
        console.error("API Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBannerData();
  }, []);

  // Typing effect logic
  useEffect(() => {
    if (!bannerData || !bannerData.isActive) return;

    // Use API words if available, otherwise use default
    const words = bannerData.typingWords || defaultWords;
    const currentWord = words[currentWordIndex];

    const handleTyping = () => {
      if (isDeleting) {
        // Deleting characters
        setCurrentText(currentWord.substring(0, currentText.length - 1));
        if (currentText.length === 1) {
          // Move to next word when deletion is complete
          setIsDeleting(false);
          setCurrentWordIndex((prevIndex) => (prevIndex + 1) % words.length);
          setTypingSpeed(bannerData.typingSpeed || 150); // Reset typing speed
        }
      } else {
        // Typing characters
        setCurrentText(currentWord.substring(0, currentText.length + 1));
        if (currentText === currentWord) {
          // Pause before deleting
          setIsDeleting(true);
          setTypingSpeed(bannerData.typingSpeed || 150); // Reset to normal typing speed
          timeoutRef.current = setTimeout(() => {
            setDeletingSpeed(bannerData.deletingSpeed || 50); // Faster deletion
          }, pauseDuration);
        }
      }
    };

    timeoutRef.current = setTimeout(
      handleTyping,
      isDeleting ? deletingSpeed : typingSpeed
    );

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [
    currentText,
    isDeleting,
    currentWordIndex,
    bannerData,
    typingSpeed,
    deletingSpeed,
    pauseDuration,
  ]);

  // Show loading state
  if (loading) {
    return (
      <section className="hero-banner-v2 position-relative z-1">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="text-center py-5">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden"></span>
                </Spinner>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section className="hero-banner-v2 position-relative z-1">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <Alert variant="danger">{error}</Alert>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Don't render if banner is inactive
  if (!bannerData || !bannerData.isActive) return null;

  return (
    <>
      <section className="hero-banner-v2 position-relative z-1">
        <div className="shape shape-one">
          <span>
            <img src="assets/images/hero/hero-two-shape-1.png" alt="" />
          </span>
        </div>
        <div className="shape shape-two">
          <span>
            <img src="assets/images/hero/hero-two-shape-2.png" alt="" />
          </span>
        </div>
        <div className="shape shape-three">
          <span>
            <img src="assets/images/hero/hero-two-shape-3.png" alt="" />
          </span>
        </div>
        <div className="shape shape-four">
          <span>
            <img src="assets/images/hero/dot-pattern.png" alt="" />
          </span>
        </div>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="hero-content">
                <span className="sub-title wow fadeInUp">
                  <i className="fas fa-arrow-right" />
                  {bannerData.subTitle}
                </span>
                <h1 className="wow fadeInUp" data-wow-delay=".5s">
                  {bannerData.mainTitle}
                </h1>
                <p className="wow fadeInDown" data-wow-delay="1s">
                  {bannerData.description}
                </p>
                <Link
                  to={bannerData.buttonLink}
                  className="main-btn arrow-btn wow fadeInUp"
                  data-wow-delay=".7s"
                >
                  {bannerData.buttonText}
                </Link>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="hero-img wow fadeInRight" data-wow-delay=".9s">
                <img
                  src={bannerData.imageUrl}
                  alt=""
                />
                <div className="hero-shape hero-shape-one animate-float-x">
                  <span>
                    <img src="assets/images/hero/shape-1.png" alt="" />
                  </span>
                </div>
                <div className="hero-shape hero-shape-two animate-float-y">
                  <span>
                    <img src="assets/images/hero/shape-2.png" alt="" />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Only show video section if YouTube URL is provided */}
      {bannerData.youtubeUrl && (
        <div className="top-feature">
          <div className="container">
            <div className="col-lg-12 col-12 mt-5">
              <div
                className="hero-video wow fadeInRight"
                data-wow-delay="0.3s"
                style={{
                  visibility: "visible",
                  animationDelay: "0.3s",
                  animationName: "fadeInRight",
                }}
              >
                <div className="video-container">
                  <div className="video-glow"></div>
                  <div className="video-badge">▶ WATCH NOW</div>
                  <div className="video-wrapper">
                    <iframe
                      src={bannerData.youtubeUrl}
                      title="YouTube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  </div>
                </div>
              </div>
            </div>
            <style jsx>{`
              .banner-description {
                font-size: 1.1rem;
                color: #000;
                max-width: 800px;
                margin: 0 auto;
                line-height: 1.6;
                font-weight: bold;
              }
              .video-container {
                max-width: 750px;
                width: 95%;
                margin: 0 auto;
                background: linear-gradient(135deg, #101128 0%, #05051e 100%);
                padding: 20px;
                border-radius: 20px;
                box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
                transform: perspective(1000px) rotateY(-5deg);
                transition: all 0.3s ease;
                box-sizing: border-box;
              }
              .video-container:hover {
                transform: perspective(1000px) rotateY(0deg) scale(1.02);
                box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
              }
              .video-wrapper {
                position: relative;
                padding-bottom: 56.25%;
                height: 0;
                overflow: hidden;
                border-radius: 15px;
                background: #000;
              }
              .video-wrapper iframe {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                border-radius: 15px;
                border: none;
              }
              .video-placeholder {
                display: flex;
                align-items: center;
                justify-content: center;
                height: 300px;
                background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
                border-radius: 15px;
                color: white;
                font-size: 18px;
                font-weight: 500;
              }
              .hero-video {
                max-width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                min-height: 300px;
                width: 100%;
                text-align: center;
                margin: 0 auto;
                padding: 0 10px;
                box-sizing: border-box;
              }
              .video-badge {
                position: absolute;
                top: -10px;
                right: -10px;
                background: #ff4757;
                color: white;
                padding: 5px 15px;
                border-radius: 20px;
                font-size: 12px;
                font-weight: bold;
                z-index: 10;
                box-shadow: 0 5px 15px rgba(255, 71, 87, 0.3);
              }
              .video-glow {
                position: absolute;
                top: -50%;
                left: -50%;
                width: 200%;
                height: 200%;
                background: radial-gradient(
                  circle,
                  rgba(102, 126, 234, 0.1) 0%,
                  transparent 70%
                );
                pointer-events: none;
                animation: pulse 3s ease-in-out infinite;
              }
              @keyframes pulse {
                0%,
                100% {
                  opacity: 0.5;
                  transform: scale(1);
                }
                50% {
                  opacity: 1;
                  transform: scale(1.1);
                }
              }
              /* Mobile Styles - Fixes for mobile responsiveness */
              @media (max-width: 768px) {
                .video-container {
                  position: relative;
                  width: 90%;
                  max-width: 90%;
                  transform: none;
                  margin: 20px auto;
                  margin-top: 30px;
                }
                .video-container:hover {
                  transform: none;
                  scale: 1;
                }
                .video-placeholder {
                  height: 200px;
                  font-size: 16px;
                }
                .hero-video {
                  min-height: 220px;
                  margin-top: 30px;
                  padding: 0 5px;
                }
                .video-badge {
                  top: -8px;
                  right: -8px;
                  padding: 4px 12px;
                  font-size: 11px;
                }
                .video-wrapper {
                  padding-bottom: 56.25%;
                }
                .banner-description {
                  font-size: 1rem;
                  padding: 0 15px;
                }
              }
              /* Extra small screens */
              @media (max-width: 480px) {
                .video-container {
                  padding: 15px;
                  margin: 15px auto;
                  margin-top: 20px;
                  border-radius: 15px;
                  width: 85%;
                  max-width: 85%;
                }
                .video-placeholder {
                  height: 180px;
                  font-size: 14px;
                  padding: 10px;
                  text-align: center;
                }
                .hero-video {
                  min-height: 200px;
                  padding: 0;
                }
                .video-wrapper {
                  border-radius: 10px;
                }
                .video-badge {
                  top: -6px;
                  right: -6px;
                  padding: 3px 10px;
                  font-size: 10px;
                }
              }
              /* Very small screens */
              @media (max-width: 360px) {
                .video-container {
                  padding: 10px;
                  margin: 10px auto;
                  width: 80%;
                  max-width: 80%;
                }
                .video-placeholder {
                  height: 160px;
                  font-size: 13px;
                }
                .hero-video {
                  min-height: 180px;
                  padding: 0;
                }
              }
              /* Typing effect styles */
              .cd-headline {
                font-size: inherit;
                line-height: inherit;
                font-weight: inherit;
              }
              .cd-words-wrapper {
                display: inline-block;
                position: relative;
                text-align: left;
                color: #ff4757; /* Color for the typing text */
              }
              .cd-words-wrapper b {
                display: inline-block;
                position: relative;
              }
              .cd-words-wrapper b.is-visible {
                position: relative;
              }
              .cursor {
                display: inline-block;
                width: 3px;
                height: 1.2em;
                background-color: #ff4757;
                animation: blink 1s infinite;
                margin-left: 2px;
                vertical-align: text-bottom;
              }
              .highlight-subtitle {
                display: inline-block;
                padding: 0.5rem 1rem;
                background: linear-gradient(90deg, #0d6efd, #6ea8fe);
                color: #fff;
                font-weight: 600;
                border-radius: 0.5rem;
                font-size: 1.25rem; /* same as Bootstrap fs-4 */
                box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
              }

              @keyframes blink {
                0%,
                50% {
                  opacity: 1;
                }
                51%,
                100% {
                  opacity: 0;
                }
              }
            `}</style>
          </div>
        </div>
      )}
    </>
  );
};

export default BannerSection;